import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  /** Optional custom fallback UI. If omitted, a default crash screen is shown. */
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Top-level error boundary that catches unhandled render errors anywhere in
 * the component tree. Without this, a single thrown error during render will
 * blank the entire app with no recovery path for the user.
 *
 * Usage:
 *   <ErrorBoundary>
 *     <App />
 *   </ErrorBoundary>
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // In production this is where you'd forward to an error tracking service
    // (e.g. Sentry). For now we surface in dev and swallow in prod.
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.error('[ErrorBoundary] Uncaught render error:', error, info.componentStack);
    }
  }

  private handleReload = () => {
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100vh',
            gap: '12px',
            fontFamily: 'system-ui, -apple-system, sans-serif',
            color: 'var(--text-primary)',
            background: 'var(--bg-base)',
            padding: '24px',
          }}
        >
          <h1 style={{ fontSize: '1.25rem', fontWeight: 600, margin: 0 }}>
            Something went wrong
          </h1>
          <p
            style={{
              fontSize: '0.875rem',
              color: 'var(--text-secondary)',
              maxWidth: 400,
              textAlign: 'center',
              margin: 0,
            }}
          >
            An unexpected error occurred. Refreshing the page usually fixes this.
          </p>
          <button
            onClick={this.handleReload}
            style={{
              marginTop: '8px',
              padding: '8px 20px',
              borderRadius: '6px',
              border: 'none',
              background: 'var(--accent)',
              color: 'var(--text-inverse)',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: 500,
            }}
          >
            Refresh page
          </button>
          {import.meta.env.DEV && this.state.error && (
            <pre
              style={{
                marginTop: '16px',
                padding: '12px',
                background: 'color-mix(in srgb, var(--error) 12%, transparent)',
                border: '1px solid color-mix(in srgb, var(--error) 40%, transparent)',
                borderRadius: '6px',
                fontSize: '0.75rem',
                maxWidth: '640px',
                width: '100%',
                overflow: 'auto',
                color: 'var(--error)',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {this.state.error.stack ?? this.state.error.message}
            </pre>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}
